import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useNavigate, useLocation, type Location } from 'react-router';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import type { Customer, LoginFormData } from '@jwp/ott-common/types/account';
import { PATH_ACTIVATETV, PATH_FORGOT_PASSWORD, PATH_HOME, PATH_SIGNUP } from '@jwp/ott-common/src/paths';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import Visibility from '@jwp/ott-theme/assets/icons/eye-on.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/eye-off.svg?react';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import { cognitoSignIn } from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { SetCookie } from '@jwp/ott-common/src/utils/cookie';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import AuthLayout from '../../containers/AuthLayout/AuthLayout';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';

import styles from './SignInCognito.module.scss';

const SignInCognito: React.FC = () => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const { siteName } = useConfigStore((s) => ({ siteName: s.config.siteName }));
  const recaptchaAPI = useConfigStore((s) => s.appConfig.lambda.recaptcha_verification);
  const user = useAccountStore((s) => s.user);
  const accountLoading = useAccountStore((s) => s.loading);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { authentication_type, isGoogleCaptchaEnabled, isShowRegisterButton } = useAppConfig();
  const deviceType = useGADeviceType();
  const announce = useAriaAnnouncer();
  const qr = useQueryParam('qr');
  const redirectParam = useQueryParam('redirect');
  const [viewPassword, toggleViewPassword] = useToggle();
  const locationState = location.state as { from?: Location } | null;
  const redirectFromState = locationState?.from ? `${locationState.from.pathname}${locationState.from.search ?? ''}${locationState.from.hash ?? ''}` : null;
  const redirectTo = redirectFromState ? redirectFromState : redirectParam ? decodeURIComponent(redirectParam) : qr ? PATH_ACTIVATETV : PATH_HOME;

  const loginSchema = useMemo(
    () =>
      object().shape({
        email: string()
          .trim()
          .required(t('login.field_required', { field: t('login.username', { defaultValue: 'Username' }) })),
        password: string()
          .trim()
          .required(t('login.field_required', { field: t('login.password') })),
      }),
    [t],
  );

  const { values, errors, submitting, validationSchemaError, handleSubmit, handleChange } = useForm<LoginFormData>({
    initialValues: { email: '', password: '' },
    silentValidation: true,
    validationSchema: loginSchema,

    onSubmit: async ({ email: username, password }) => {
      // 1. ReCaptcha validation if enabled
      if (isGoogleCaptchaEnabled) {
        if (!executeRecaptcha) {
          console.warn('reCAPTCHA not ready');
          throw new Error('reCAPTCHA not ready');
        }

        const token = await executeRecaptcha('login');
        const verifyResponse = await fetch(recaptchaAPI, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const { success, score, action } = await verifyResponse.json();

        if (!success || score < 0.5 || action !== 'login') {
          throw new Error('reCAPTCHA validation failed');
        }
      }

      // 2. Katon Cognito Sign In
      const signInData = await cognitoSignIn(username, password);

      if (!signInData?.AccessToken) {
        throw new Error(t('login.unexpected_error'));
      }

      SetCookie('AccessToken', signInData.AccessToken);
      SetCookie('RefreshToken', signInData.RefreshToken);
      SetCookie('IdToken', signInData.IdToken);

      // const authData = {
      //   accessToken: signInData.AccessToken,
      //   refreshToken: signInData.RefreshToken,
      // };

      // localStorage.setItem('jwapp.auth', JSON.stringify(authData));

      SetCookie('InplayerToken', {
        token: signInData.AccessToken,
        refreshToken: signInData.RefreshToken,
      });

      // 4. Restore session via AccountController — fetches user, watch history & favorites
      await getModule(AccountController).restoreKatonCognitoSession();

      if (!useAccountStore.getState().user) {
        throw new Error(t('login.unexpected_error'));
      }
    },
    onSubmitSuccess: () => {
      const user = useAccountStore.getState().user;
      sendGAEvent('login_success', {
        provider: authentication_type,
        method: authentication_type,
        userid_no: user?.id,
        device_type: deviceType,
      });

      announce(t('login.sign_in_success'), 'success');
      navigate(redirectTo, { replace: true });
    },
    onSubmitError: ({ error, resetValue }) => {
      let errorMessage = 'Unknown error';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      sendGAEvent('login_failure', {
        email: values.email,
        error_message: errorMessage,
        device_type: deviceType,
      });

      showToast(errorMessage, 'error');
      resetValue('password');
    },
  });

  useEffect(() => {
    if (user && !accountLoading) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, user, accountLoading, redirectTo]);

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <AuthLayout title={t('login.sign_in', 'Sign In')} subtitle={t('login.sign_in_subtitle', 'Sign in to your play account')}>
      <Helmet>
        <title>
          {t('login.sign_in', 'Sign In')} | {siteName}
        </title>
      </Helmet>

      <div className={styles.overlayWrap}>
        {submitting && (
          <div className={styles.loaderLayer}>
            <LoadingOverlay inline transparentBackground />
          </div>
        )}

        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formBody}>
              <div className={styles.field}>
                <label htmlFor="email" className={styles.fieldLabel}>
                  {t('login.username', 'Username / Email ID')}
                  <span className={styles.required} aria-hidden="true">
                    *
                  </span>
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="email"
                    name="email"
                    type="text"
                    autoComplete="username"
                    className={styles.input}
                    value={values.email}
                    onChange={handleChange}
                    placeholder={t('login.username_placeholder', { defaultValue: 'Enter username / email id' })}
                    required
                    aria-invalid={!!errors.email}
                  />
                </div>
                {errors.email ? <p className={styles.helperError}>{errors.email}</p> : null}
              </div>

              <div className={styles.field}>
                <label htmlFor="password" className={styles.fieldLabel}>
                  {t('login.password', 'Password')}
                  <span className={styles.required} aria-hidden="true">
                    *
                  </span>
                </label>
                <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
                  <input
                    id="password"
                    name="password"
                    type={viewPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={styles.input}
                    value={values.password}
                    onChange={handleChange}
                    placeholder={t('login.password_placeholder', { defaultValue: 'Enter password' })}
                    required
                    aria-invalid={!!errors.password}
                  />
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => toggleViewPassword()}
                    aria-label={t('login.view_password')}
                    aria-pressed={viewPassword}
                  >
                    <Icon icon={viewPassword ? Visibility : VisibilityOff} />
                  </button>
                </div>
                {errors.password ? <p className={styles.helperError}>{errors.password}</p> : null}
              </div>

              <div className={styles.forgotLinks}>
                <button type="button" className={styles.forgotLink} onClick={() => navigate(PATH_FORGOT_PASSWORD)}>
                  {t('login.forgot_link', 'Forgot Username / Password?')}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              color="primary"
              className={styles.submit}
              disabled={submitting}
              label={submitting ? t('login.signing_in', 'Signing In...') : t('login.sign_in', 'Sign In')}
              fullWidth
            />
            {isShowRegisterButton && (
              <p className={styles.footer}>
                {t('login.dont_have_account', { defaultValue: "Don't have an account?" })}
                <button type="button" className={styles.signUpLink} onClick={() => navigate(PATH_SIGNUP)}>
                  {t('login.sign_up', 'Sign Up')}
                </button>
              </p>
            )}
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignInCognito;
